import React, { useState, useEffect } from 'react';
import { regulatoryReportsApi } from '../lib/api';
import api from '../lib/api';
import { formatCurrency, formatDateTime, getStatusColor, capitalizeFirst } from '../lib/utils';
import {
  FileBarChart, Plus, Download, Eye, Send, Calendar, AlertCircle, CheckCircle2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const ReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [reportType, setReportType] = useState('BA100');
  const [viewingReport, setViewingReport] = useState(null);
  const [submittingId, setSubmittingId] = useState(null);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      const response = await regulatoryReportsApi.list();
      setReports(response.data);
    } catch (error) {
      console.error('Failed to load reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (report) => {
    try {
      const res = await api.get(`/regulatory-reports/${report.id}`);
      setViewingReport(res.data);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to load report');
    }
  };

  const handleDownload = async (report, format = 'json') => {
    try {
      const res = await api.get(`/regulatory-reports/${report.id}/download`, {
        params: { format },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${report.report_type}_${(report.reporting_period || '').replace(/\//g, '-')}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Download failed');
    }
  };

  const handleSubmit = async (report) => {
    if (!window.confirm(`Submit ${report.report_type} for ${report.reporting_period} to the regulator? This cannot be undone.`)) return;
    setSubmittingId(report.id);
    try {
      await regulatoryReportsApi.submit(report.id);
      await loadReports();
    } catch (e) {
      alert(e.response?.data?.detail || 'Submit failed');
    }
    setSubmittingId(null);
  };

  return (
    <div className="space-y-6 fade-in" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-2">
            REGULATORY COMPLIANCE
          </p>
          <h1 className="font-display text-3xl font-bold text-[#1f1f1f]">
            Reports
          </h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={() => { setReportType('BA100'); setShowGenerateModal(true); }}
            className="bg-[#1f1f1f] hover:bg-[#333333]"
            data-testid="generate-ba100-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            BA100
          </Button>
          <Button
            onClick={() => { setReportType('BA110'); setShowGenerateModal(true); }}
            variant="outline"
            className="border-[#1f1f1f] text-[#1f1f1f]"
            data-testid="generate-ba110-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            BA110
          </Button>
          <Button
            onClick={() => { setReportType('BA120'); setShowGenerateModal(true); }}
            variant="outline"
            className="border-[#1f1f1f] text-[#1f1f1f]"
            data-testid="generate-ba120-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            BA120
          </Button>
          <Button
            onClick={() => { setReportType('CFI'); setShowGenerateModal(true); }}
            variant="outline"
            className="border-[#C5A059] text-[#C5A059]"
            data-testid="generate-cfi-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            CFI
          </Button>
          <Button
            onClick={() => { setReportType('MICROFINANCE'); setShowGenerateModal(true); }}
            variant="outline"
            className="border-[#C5A059] text-[#C5A059]"
            data-testid="generate-microfinance-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Microfinance
          </Button>
          <Button
            onClick={() => { setReportType('DTI_STOKVEL'); setShowGenerateModal(true); }}
            variant="outline"
            className="border-[#C5A059] text-[#C5A059]"
            data-testid="generate-dti-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            DTI/Stokvel
          </Button>
        </div>
      </div>

      {/* Report Types */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { type: 'BA100', label: 'Balance Sheet Return', desc: 'Monthly submission to SARB Prudential Authority' },
          { type: 'BA110', label: 'Off-Balance Sheet', desc: 'Contingent liabilities, commitments & guarantees' },
          { type: 'BA120', label: 'Income Statement', desc: 'Revenue, expenses & profitability reporting' },
        ].map(rt => (
          <div key={rt.type} className="p-6 border border-gray-200 hover:border-[#C5A059] transition-colors cursor-pointer" onClick={() => { setReportType(rt.type); setShowGenerateModal(true); }} data-testid={`report-card-${rt.type.toLowerCase()}`}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-[#1f1f1f]">{rt.type}</h3>
                <p className="text-sm text-gray-500 mt-1">{rt.label}</p>
              </div>
              <FileBarChart className="w-8 h-8 text-[#C5A059]" />
            </div>
            <p className="text-xs text-gray-500 mt-4">{rt.desc}</p>
          </div>
        ))}
      </div>

      {/* Institutional Returns */}
      <div className="mt-2">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Institutional Returns</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { type: 'CFI', label: 'Co-operative Financial Institution', desc: 'CBDA/SARB prudential reporting for SACCOs and credit unions', endpoint: 'cfi' },
            { type: 'MICROFINANCE', label: 'Microfinance Institution (NCR)', desc: 'NCR compliance: affordability assessments, interest rate caps, arrears', endpoint: 'microfinance' },
            { type: 'DTI_STOKVEL', label: 'DTI / Stokvel Return', desc: 'Deposit-taking institution: contributions, payouts, fund utilization', endpoint: 'dti-stokvel' },
          ].map(rt => (
            <div key={rt.type} className="p-6 border border-gray-200 hover:border-[#C5A059] transition-colors cursor-pointer" onClick={() => { setReportType(rt.type); setShowGenerateModal(true); }} data-testid={`report-card-${rt.type.toLowerCase()}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-[#1f1f1f] text-sm">{rt.type.replace(/_/g, '/')}</h3>
                  <p className="text-sm text-gray-500 mt-1">{rt.label}</p>
                </div>
                <FileBarChart className="w-8 h-8 text-[#1f1f1f]" />
              </div>
              <p className="text-xs text-gray-500 mt-4">{rt.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* FIC Act Reports */}
      <div className="mt-2">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">FIC Act (Financial Intelligence Centre)</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { type: 'FIC_CTR', label: 'Cash Threshold Report (Form A)', desc: 'Cash transactions ≥ R 49,999.99 — file within 2 working days', endpoint: 'fic/ctr' },
            { type: 'FIC_STR', label: 'Suspicious Transaction Report (Form C)', desc: 'Flagged transactions + sanctions escalations — file within 15 working days', endpoint: 'fic/str' },
            { type: 'FIC_IFTR', label: 'Intl Funds Transfer Report (Form B)', desc: 'Cross-border transfers ≥ R 19,999.99 — file within 3 working days', endpoint: 'fic/iftr' },
            { type: 'FIC_SANCTIONS_AUDIT', label: 'Sanctions / PEP Screening Audit', desc: 'Quarterly internal: screenings, false-positives, blocks, list freshness', endpoint: 'fic/sanctions-audit' },
            { type: 'FIC_PEP_REGISTER', label: 'PEP Register', desc: 'Quarterly register of PEP clients onboarded with EDD evidence', endpoint: 'fic/pep-register' },
            { type: 'FIC_KYC_COMPLIANCE', label: 'KYC / CDD Compliance', desc: 'Verification % per client (ID, address, employer, income, docs, screening)', endpoint: 'fic/kyc-compliance' },
          ].map(rt => (
            <div key={rt.type} className="p-6 border border-gray-200 hover:border-[#C5A059] transition-colors cursor-pointer" onClick={() => { setReportType(rt.type); setShowGenerateModal(true); }} data-testid={`report-card-${rt.type.toLowerCase()}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-[#1f1f1f] text-sm">{rt.label.split('(')[0].trim()}</h3>
                  <p className="text-xs text-gray-400 mt-1 uppercase tracking-wider">{rt.type.replace(/_/g, ' ')}</p>
                </div>
                <FileBarChart className="w-8 h-8 text-emerald-600" />
              </div>
              <p className="text-xs text-gray-500 mt-4">{rt.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* NCR Statutory Returns */}
      <div className="mt-2">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">NCR Statutory Returns (National Credit Act)</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { type: 'NCR_FORM39', label: 'Form 39 (Form J) — Statistical', desc: 'Quarterly: applications received/approved/rejected/disbursed + arrears bands', endpoint: 'ncr/form39' },
            { type: 'NCR_CR1', label: 'CR1 — Annual Compliance', desc: 'Registered details, branches, fee policy, year-end book size', endpoint: 'ncr/cr1' },
            { type: 'NCR_CR2', label: 'CR2 — Quarterly Disbursement', desc: 'Credit advanced by sub-sector + NCA rate-cap compliance evidence', endpoint: 'ncr/cr2' },
          ].map(rt => (
            <div key={rt.type} className="p-6 border border-gray-200 hover:border-[#C5A059] transition-colors cursor-pointer" onClick={() => { setReportType(rt.type); setShowGenerateModal(true); }} data-testid={`report-card-${rt.type.toLowerCase()}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-[#1f1f1f] text-sm">{rt.label.split('—')[0].trim()}</h3>
                  <p className="text-xs text-gray-400 mt-1 uppercase tracking-wider">{rt.type.replace(/_/g, ' ')}</p>
                </div>
                <FileBarChart className="w-8 h-8 text-amber-600" />
              </div>
              <p className="text-xs text-gray-500 mt-4">{rt.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Generated Reports */}
      <div className="bg-white border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-[#1f1f1f]">Generated Reports</h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-gray-200 border-t-[#1f1f1f] rounded-full animate-spin" />
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-12">
            <FileBarChart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No reports generated yet</p>
            <p className="text-sm text-gray-400 mt-1">Generate your first BA100 report</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="reports-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Report Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Period
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Generated
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {reports.map((report) => (
                  <tr key={report.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">{report.report_type}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{report.reporting_period}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{formatDateTime(report.created_at)}</td>
                    <td className="px-6 py-4 text-center">
                      <span className={`px-2.5 py-0.5 text-xs font-medium ${getStatusColor(report.status)}`}>
                        {capitalizeFirst(report.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleView(report)} title="View report" data-testid={`view-report-${report.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDownload(report, 'json')} title="Download JSON" data-testid={`download-json-${report.id}`}>
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDownload(report, 'csv')} title="Download CSV" data-testid={`download-csv-${report.id}`} className="text-xs px-2">
                          CSV
                        </Button>
                        {report.status === 'draft' && (
                          <Button variant="ghost" size="sm" className="text-[#C5A059]" onClick={() => handleSubmit(report)} disabled={submittingId === report.id} title="Submit to regulator" data-testid={`submit-report-${report.id}`}>
                            <Send className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Generate Report Modal */}
      <GenerateReportModal
        open={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        reportType={reportType}
        onSuccess={() => {
          setShowGenerateModal(false);
          loadReports();
        }}
      />

      {/* View Report Modal */}
      <Dialog open={!!viewingReport} onOpenChange={(o) => !o && setViewingReport(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" aria-describedby="view-report-desc" data-testid="view-report-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <FileBarChart className="w-6 h-6 text-[#C5A059]" />
              {viewingReport?.report_type} — {viewingReport?.reporting_period}
            </DialogTitle>
          </DialogHeader>
          <p id="view-report-desc" className="sr-only">Saved regulatory report contents and download options.</p>
          {viewingReport && (
            <div className="space-y-4">
              <div className="text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                <span>Status: <strong className={`uppercase ${viewingReport.status === 'submitted' ? 'text-emerald-700' : 'text-amber-700'}`}>{viewingReport.status}</strong></span>
                <span>Created: {viewingReport.created_at && new Date(viewingReport.created_at).toLocaleString()}</span>
                {viewingReport.submitted_at && <span>Submitted: {new Date(viewingReport.submitted_at).toLocaleString()}</span>}
              </div>
              {Object.entries((viewingReport.data || {}).sections || viewingReport.data || {}).map(([sk, sd]) => (
                <ReportSection key={sk} title={sk} data={sd} onManualEntry={() => {}} sectionKey={sk} />
              ))}
              {(!viewingReport.data || Object.keys(viewingReport.data).length === 0) && (
                <p className="text-gray-400 text-center py-6">This report has no saved data.</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewingReport(null)}>Close</Button>
            <Button onClick={() => handleDownload(viewingReport, 'json')} className="bg-[#1f1f1f] hover:bg-[#333333]"><Download className="w-4 h-4 mr-1" />Download JSON</Button>
            <Button onClick={() => handleDownload(viewingReport, 'csv')} variant="outline"><Download className="w-4 h-4 mr-1" />Download CSV</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const GenerateReportModal = ({ open, onClose, onSuccess, reportType = 'BA100' }) => {
  const [period, setPeriod] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [manualEntries, setManualEntries] = useState({});

  const generateReport = async () => {
    if (!period) return;
    setLoading(true);
    try {
      const endpointMap = {
        'BA110': 'ba110', 'BA120': 'ba120',
        'CFI': 'cfi', 'MICROFINANCE': 'microfinance', 'DTI_STOKVEL': 'dti-stokvel',
        'FIC_CTR': 'fic/ctr', 'FIC_STR': 'fic/str', 'FIC_IFTR': 'fic/iftr',
        'FIC_SANCTIONS_AUDIT': 'fic/sanctions-audit',
        'FIC_PEP_REGISTER': 'fic/pep-register',
        'FIC_KYC_COMPLIANCE': 'fic/kyc-compliance',
        'NCR_FORM39': 'ncr/form39', 'NCR_CR1': 'ncr/cr1', 'NCR_CR2': 'ncr/cr2',
      };
      const endpoint = endpointMap[reportType] || 'ba100';
      const response = await api.get(`/regulatory-reports/${endpoint}/generate`, { params: { reporting_period: period } });
      setReportData(response.data);
    } catch (error) {
      console.error('Failed to generate report:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveReport = async () => {
    setSaving(true);
    try {
      // Merge auto-populated and manual entries
      const finalData = {
        report_type: reportType,
        reporting_period: period,
        data: {
          ...reportData,
          manual_entries: manualEntries
        }
      };
      await regulatoryReportsApi.save(finalData);
      onSuccess();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  const updateManualEntry = (section, field, value) => {
    setManualEntries(prev => ({
      ...prev,
      [`${section}.${field}`]: parseFloat(value) || 0
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <FileBarChart className="w-6 h-6 text-[#C5A059]" />
            Generate {reportType} Report
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Period Selection */}
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <Label className="text-xs uppercase tracking-wider text-gray-500">Reporting Period</Label>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger data-testid="report-period-select">
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2025-Q4">Q4 2025 (Oct - Dec)</SelectItem>
                  <SelectItem value="2025-Q3">Q3 2025 (Jul - Sep)</SelectItem>
                  <SelectItem value="2025-Q2">Q2 2025 (Apr - Jun)</SelectItem>
                  <SelectItem value="2025-Q1">Q1 2025 (Jan - Mar)</SelectItem>
                  <SelectItem value="2024-Q4">Q4 2024 (Oct - Dec)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={generateReport} disabled={!period || loading} className="bg-[#1f1f1f]" data-testid="generate-ba100-button">
              {loading ? 'Generating...' : 'Generate Report'}
            </Button>
          </div>

          {/* Report Data */}
          {reportData && (
            <div className="space-y-6">
              <div className="p-4 bg-blue-50 border border-blue-200 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium">{reportData.report_name || `${reportType} Report`}</p>
                  <p className="text-blue-600 mt-1">Generated at {reportData.generated_at ? new Date(reportData.generated_at).toLocaleString() : 'now'}</p>
                </div>
              </div>

              {/* Render sections generically */}
              {Object.entries(reportData.sections || {}).map(([sectionKey, sectionData]) => (
                <ReportSection key={sectionKey} title={sectionKey} data={sectionData} onManualEntry={updateManualEntry} sectionKey={sectionKey} />
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          {reportData && (
            <Button onClick={saveReport} disabled={saving} className="bg-[#1f1f1f] hover:bg-[#333333]" data-testid="save-report-button">
              {saving ? 'Saving...' : 'Save as Draft'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReportsPage;

const ReportSection = ({ title, data, onManualEntry, sectionKey }) => {
  const formatLabel = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  if (data === null || data === undefined) return null;

  // If it's a primitive value (number, string)
  if (typeof data !== 'object' || Array.isArray(data)) {
    return (
      <div className="border border-gray-200">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <h4 className="font-semibold text-[#1f1f1f] uppercase text-sm">{formatLabel(title)}</h4>
        </div>
        <div className="px-4 py-3">
          {Array.isArray(data) ? (
            data.length === 0 ? <p className="text-sm text-gray-400">No data</p> :
            typeof data[0] === 'object' ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    {Object.keys(data[0]).map(key => (
                      <th key={key} className="text-left py-2 px-2 text-xs font-semibold text-gray-500 uppercase">{key.replace(/_/g, ' ')}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, i) => (
                    <tr key={i} className="border-b border-gray-100 last:border-0">
                      {Object.values(item).map((val, j) => (
                        <td key={j} className="py-2 px-2 text-gray-700">
                          {typeof val === 'number' ? val.toLocaleString('en-ZA') : String(val ?? '-')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) :
            <div className="space-y-1">{data.map((item, i) => <p key={i} className="text-sm text-gray-700">{String(item)}</p>)}</div>
          ) : (
            <p className="text-lg font-semibold text-[#1f1f1f]">{typeof data === 'number' ? `R ${data.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}` : String(data)}</p>
          )}
        </div>
      </div>
    );
  }

  // If it has 'value' and 'manual_entry_required' keys -> it's a single field item
  if ('value' in data || 'manual_entry_required' in data) {
    return null; // handled inline
  }

  // Object with sub-fields
  return (
    <div className="border border-gray-200">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <h4 className="font-semibold text-[#1f1f1f] uppercase text-sm">{formatLabel(title)}</h4>
      </div>
      <div className="divide-y divide-gray-100">
        {Object.entries(data).map(([key, item]) => {
          if (item === null || item === undefined) return null;
          // Sub-object with auto/value/manual_entry_required
          if (typeof item === 'object' && !Array.isArray(item) && ('value' in item || 'manual_entry_required' in item)) {
            return (
              <div key={key} className="px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">{formatLabel(key)}</span>
                  {item.manual_entry_required && <span className="text-xs text-amber-600 font-medium">*</span>}
                  {item.auto && !item.manual_entry_required && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                </div>
                <div className="w-48">
                  {item.manual_entry_required ? (
                    <Input type="number" placeholder="Enter value" onChange={(e) => onManualEntry(sectionKey, key, e.target.value)} className="text-right" />
                  ) : (
                    <p className="text-right font-medium">R {(item.value || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</p>
                  )}
                </div>
              </div>
            );
          }
          // Sub-object (nested section)
          if (typeof item === 'object' && !Array.isArray(item)) {
            return (
              <div key={key} className="px-4 py-3">
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">{formatLabel(key)}</p>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(item).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm">
                      <span className="text-gray-600">{formatLabel(k)}</span>
                      <span className="font-medium">{typeof v === 'number' ? v.toLocaleString('en-ZA', { minimumFractionDigits: 2 }) : String(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          }
          // Simple key-value
          return (
            <div key={key} className="px-4 py-3 flex items-center justify-between">
              <span className="text-sm text-gray-700">{formatLabel(key)}</span>
              <span className="text-sm font-medium text-[#1f1f1f]">{typeof item === 'number' ? item.toLocaleString('en-ZA', { minimumFractionDigits: typeof item === 'number' && item % 1 !== 0 ? 2 : 0 }) : String(item)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
